import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CreditCard, Palette, Truck, Mail, Wallet, CheckCircle2, XCircle, CalendarClock } from 'lucide-react';

function Card({ title, icon: Icon, children, note }) {
  return (
    <div className="border border-border p-5 md:p-6" data-testid={`card-${title}`}>
      <div className="flex items-center gap-3 mb-1">
        {Icon && <Icon size={18} strokeWidth={1.3} />}
        <h3 className="font-display text-lg md:text-xl">{title}</h3>
      </div>
      {note && <p className="text-xs font-light text-muted-foreground mb-4">{note}</p>}
      <div className="space-y-3 mt-4">{children}</div>
    </div>
  );
}

function F({ label, value, onChange, placeholder, type = 'text', testId }) {
  return (
    <div>
      <Label className="overline">{label}</Label>
      <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 rounded-none border-0 border-b border-foreground focus-visible:ring-0" data-testid={testId} />
    </div>
  );
}

export default function AdminSettings() {
  const { api } = useApp();
  const [s, setS] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState({});

  const load = async () => {
    try { const { data } = await api.get('/admin/settings'); setS(data || {}); }
    catch { toast.error('Could not load settings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const upd = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Strip masked fields — user must retype to change
      const payload = { ...s };
      ['stripe_secret_key', 'paypal_client_secret', 'resend_api_key'].forEach((k) => {
        if (typeof payload[k] === 'string' && payload[k].startsWith('****')) delete payload[k];
      });
      delete payload.stripe_secret_key_set; delete payload.paypal_client_secret_set; delete payload.resend_api_key_set;
      delete payload.updated_at;
      await api.put('/admin/settings', payload);
      toast.success('Settings saved — payment keys are live now');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const test = async (provider) => {
    setTestResults((r) => ({ ...r, [provider]: { loading: true } }));
    // Auto-save current form state first so users don't have to remember to save before testing
    try {
      const payload = { ...s };
      ['stripe_secret_key', 'paypal_client_secret', 'resend_api_key'].forEach((k) => {
        if (typeof payload[k] === 'string' && payload[k].startsWith('****')) delete payload[k];
      });
      delete payload.stripe_secret_key_set; delete payload.paypal_client_secret_set; delete payload.resend_api_key_set; delete payload.updated_at;
      await api.put('/admin/settings', payload);
    } catch (e) {
      setTestResults((r) => ({ ...r, [provider]: { ok: false, message: 'Save failed before test — check network' } }));
      return;
    }
    try {
      const { data } = await api.post(`/admin/settings/test/${provider}`);
      setTestResults((r) => ({ ...r, [provider]: data }));
      if (data.ok) toast.success(`${provider}: ${data.message}`); else toast.error(`${provider}: ${data.message}`);
    } catch (e) {
      setTestResults((r) => ({ ...r, [provider]: { ok: false, message: e.response?.data?.detail || 'Request failed' } }));
    }
  };

  if (loading) return <div className="font-light text-muted-foreground" data-testid="settings-loading">Loading settings…</div>;

  const StatusPill = ({ result }) => {
    if (!result) return null;
    if (result.loading) return <span className="overline text-muted-foreground">testing…</span>;
    return result.ok
      ? <span className="inline-flex items-center gap-1 overline text-accent"><CheckCircle2 size={14} strokeWidth={1.4} /> connected</span>
      : <span className="inline-flex items-center gap-1 overline text-destructive"><XCircle size={14} strokeWidth={1.4} /> {result.message?.slice(0, 40)}</span>;
  };

  return (
    <div data-testid="admin-settings">
      <h2 className="font-display text-2xl mb-2">Site Settings</h2>
      <p className="text-sm font-light text-muted-foreground mb-8">All settings save to the database and apply live — no server restart needed. Payment secrets are stored masked; retype to change.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
        <Card title="Branding" icon={Palette}>
          <F label="Site Name" value={s.site_name} onChange={(v) => upd('site_name', v)} placeholder="JJBSTYLISH" testId="s-name" />
          <F label="Logo URL (optional)" value={s.logo_url} onChange={(v) => upd('logo_url', v)} placeholder="https://…" testId="s-logo" />
          <F label="Accent Color (hex)" value={s.accent_color} onChange={(v) => upd('accent_color', v)} placeholder="#8C7355" testId="s-accent" />
          <div>
            <Label className="overline">Footer Tagline</Label>
            <Textarea value={s.footer_tagline || ''} onChange={(e) => upd('footer_tagline', e.target.value)} rows={2} className="mt-1 rounded-none" data-testid="s-tagline" />
          </div>
        </Card>

        <Card title="Contact & Policies" icon={Truck}>
          <F label="Contact Email" value={s.contact_email} onChange={(v) => upd('contact_email', v)} placeholder="concierge@jjbstylish.com" testId="s-cemail" />
          <F label="Contact Phone" value={s.contact_phone} onChange={(v) => upd('contact_phone', v)} placeholder="+1 (888) 555-JJBS" testId="s-cphone" />
          <F label="Support Hours" value={s.contact_hours} onChange={(v) => upd('contact_hours', v)} placeholder="Mon–Sun · 9am–10pm ET" testId="s-chours" />
          <F label="Free Shipping Threshold (USD)" type="number" value={s.free_shipping_threshold} onChange={(v) => upd('free_shipping_threshold', parseFloat(v) || 0)} placeholder="500" testId="s-freeship" />
          <F label="Return Window (days)" type="number" value={s.return_window_days} onChange={(v) => upd('return_window_days', parseInt(v, 10) || 0)} placeholder="14" testId="s-return" />
        </Card>

        <Card title="Stripe Payments" icon={CreditCard} note="Live-applied when saved. Paste your Stripe secret key (sk_live_… or sk_test_…).">
          <F label={s.stripe_secret_key_set ? "Stripe Secret Key (retype to change)" : "Stripe Secret Key"} value={s.stripe_secret_key} onChange={(v) => upd('stripe_secret_key', v)} placeholder="sk_test_…" type="password" testId="s-stripe" />
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => test('stripe')} className="overline link-underline" data-testid="test-stripe">Test connection</button>
            <StatusPill result={testResults.stripe} />
          </div>
        </Card>

        <Card title="PayPal Payments" icon={Wallet} note="Sandbox for testing, Live for production. Get creds at developer.paypal.com.">
          <div>
            <Label className="overline">Mode</Label>
            <select value={s.paypal_mode || 'sandbox'} onChange={(e) => upd('paypal_mode', e.target.value)} className="mt-1 w-full border-0 border-b border-foreground bg-transparent py-2 font-light" data-testid="s-pp-mode">
              <option value="sandbox">Sandbox (testing)</option>
              <option value="live">Live (production)</option>
            </select>
          </div>
          <F label="Client ID" value={s.paypal_client_id} onChange={(v) => upd('paypal_client_id', v)} placeholder="AeA…" testId="s-pp-id" />
          <F label={s.paypal_client_secret_set ? "Client Secret (retype to change)" : "Client Secret"} value={s.paypal_client_secret} onChange={(v) => upd('paypal_client_secret', v)} placeholder="EG…" type="password" testId="s-pp-secret" />
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => test('paypal')} className="overline link-underline" data-testid="test-paypal">Test connection</button>
            <StatusPill result={testResults.paypal} />
          </div>
        </Card>

        <Card title="Resend (Emails)" icon={Mail} note="Sends order + consignment confirmations. Get a free API key at resend.com.">
          <F label={s.resend_api_key_set ? "Resend API Key (retype to change)" : "Resend API Key"} value={s.resend_api_key} onChange={(v) => upd('resend_api_key', v)} placeholder="re_…" type="password" testId="s-resend" />
          <F label="Sender Email (must be a verified domain in Resend)" value={s.from_email} onChange={(v) => upd('from_email', v)} placeholder="concierge@jjbstylish.com" testId="s-from" />
          <div className="flex items-center justify-between pt-2">
            <button onClick={() => test('resend')} className="overline link-underline" data-testid="test-resend">Send test email</button>
            <StatusPill result={testResults.resend} />
          </div>
        </Card>

        <Card title="Zelle" icon={Wallet} note="Shown to buyers who choose 'Bank / P2P Transfer' at checkout. Buyer sends money manually and includes the reference code.">
          <F label="Zelle Handle (email or phone)" value={s.zelle_handle} onChange={(v) => upd('zelle_handle', v)} placeholder="payments@jjbstylish.com" testId="s-zelle-h" />
          <F label="Display Label (optional)" value={s.zelle_label} onChange={(v) => upd('zelle_label', v)} placeholder="Zelle to payments@jjbstylish.com" testId="s-zelle-l" />
        </Card>

        <Card title="Venmo" icon={Wallet}>
          <F label="Venmo Handle" value={s.venmo_handle} onChange={(v) => upd('venmo_handle', v)} placeholder="@jjbstylish" testId="s-venmo-h" />
          <F label="Display Label (optional)" value={s.venmo_label} onChange={(v) => upd('venmo_label', v)} placeholder="Venmo @jjbstylish" testId="s-venmo-l" />
        </Card>

        <Card title="CashApp" icon={Wallet}>
          <F label="CashApp Handle" value={s.cashapp_handle} onChange={(v) => upd('cashapp_handle', v)} placeholder="$jjbstylish" testId="s-cashapp-h" />
          <F label="Display Label (optional)" value={s.cashapp_label} onChange={(v) => upd('cashapp_label', v)} placeholder="CashApp $jjbstylish" testId="s-cashapp-l" />
        </Card>

        <Card title="Bank Wire (optional)" icon={CreditCard} note="Freeform bank wire details shown to high-ticket buyers. Leave blank to disable.">
          <div>
            <Label className="overline">Wire Instructions</Label>
            <Textarea value={s.bank_wire_details || ''} onChange={(e) => upd('bank_wire_details', e.target.value)} rows={4} className="mt-1 rounded-none" placeholder="Bank: … / Account: … / Routing: … / SWIFT: …" data-testid="s-wire" />
          </div>
        </Card>

        <Card title="Catalog Export / Import" icon={Truck} note="Move products between environments (e.g. preview → production). Export downloads a JSON file; Import uploads one. Perfect for migrating your 50+ items after publishing.">
          <div className="flex flex-col gap-3">
            <button type="button" onClick={async () => {
              try {
                const { data } = await api.get('/admin/export/products');
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `jjbstylish-products-${new Date().toISOString().slice(0,10)}.json`; a.click();
                URL.revokeObjectURL(url);
                toast.success(`Exported ${data.count} products`);
              } catch (e) { toast.error('Export failed'); }
            }} className="bg-foreground text-background px-5 py-3 overline w-full sm:w-auto" data-testid="export-products">Download Products JSON</button>

            <label className="inline-flex items-center justify-center border border-foreground px-5 py-3 overline cursor-pointer hover:bg-foreground hover:text-background transition" data-testid="import-products-label">
              Upload Products JSON
              <input type="file" accept="application/json,.json" className="hidden" data-testid="import-products-input" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                e.target.value = '';
                try {
                  const text = await file.text();
                  const parsed = JSON.parse(text);
                  const products = parsed.products || (Array.isArray(parsed) ? parsed : []);
                  if (!products.length) { toast.error('No products found in file'); return; }
                  const replace = window.confirm(`Import ${products.length} products?\n\nOK = ADD to existing catalog (skips duplicate IDs)\nCancel = do nothing`);
                  if (!replace) return;
                  const { data: r } = await api.post('/admin/import/products', { products, replace: false, skip_existing_ids: true });
                  toast.success(`Imported ${r.inserted} products (${r.skipped} skipped)`);
                } catch (err) { toast.error('Import failed: ' + (err.message || err)); }
              }} />
            </label>
            <p className="text-xs font-light text-muted-foreground">Tip: If you need production images too, contact Emergent Support to clone your preview MongoDB + uploads volume — it's a one-click operation on their side.</p>
          </div>
        </Card>

        <Card title="Recover Lost Products from Uploads" icon={Truck} note="If product metadata (titles/prices) was lost but the uploaded IMAGES are still on disk, this scans /uploads/ for orphan images and creates DRAFT products (hidden from storefront) so you can quickly re-list them.">
          <div className="flex flex-col gap-3">
            <button type="button" onClick={async () => {
              try {
                const { data: dry } = await api.post('/admin/recover/orphan-uploads', { dry_run: true });
                const n = dry.recoverable_groups || 0;
                if (n === 0) { toast.info('No orphan uploads found to recover.'); return; }
                if (!window.confirm(`Found ${n} orphan image group(s) with an original upload.\n\nCreate ${n} DRAFT products (hidden, in_stock=false)?\nYou can then edit titles/prices/designers in the Products tab.`)) return;
                const { data: r } = await api.post('/admin/recover/orphan-uploads', { dry_run: false });
                toast.success(`Recovered ${r.created} draft products. Go to Products tab to fill in details and publish.`);
              } catch (err) {
                toast.error('Recovery failed: ' + (err?.response?.data?.detail || err.message || err));
              }
            }} className="bg-foreground text-background px-5 py-3 overline w-full sm:w-auto" data-testid="recover-orphans-btn">Scan &amp; Recover Orphan Uploads</button>
            <p className="text-xs font-light text-muted-foreground">Only groups containing the original uploaded photo are recovered. Standalone AI derivatives (try-on / studio) with no parent are skipped. Recovered drafts are marked <code>in_stock: false</code> and hidden from the storefront until you edit and publish them.</p>
          </div>
        </Card>

        <Card title="Hide Broken Storefront Items" icon={Truck} note="One-click cleanup for products that ended up with NO images or $0 price (usually leftovers from lost uploads or accidental saves). This does NOT delete anything — items are just set to in_stock=false so they disappear from the public storefront until you edit them.">
          <div className="flex flex-col gap-3">
            <button type="button" onClick={async () => {
              if (!window.confirm('Hide every product that has no images or $0 price from the storefront?\n\nNothing is deleted — you can re-publish each one after editing.')) return;
              try {
                const { data: r } = await api.post('/admin/products/hide-broken');
                toast.success(`Hidden ${r.hidden} broken product(s). Edit them in the Products tab, then re-enable "Published".`);
              } catch (err) {
                toast.error('Hide failed: ' + (err?.response?.data?.detail || err.message || err));
              }
            }} className="bg-foreground text-background px-5 py-3 overline w-full sm:w-auto" data-testid="hide-broken-btn">Hide Products With No Image or $0 Price</button>
            <p className="text-xs font-light text-muted-foreground">Use this after a re-deploy if your live storefront starts showing broken image icons or $0 items. Fix each product (upload a fresh photo + set price) then tick "Published" to bring it back.</p>
          </div>
        </Card>

        <Card title="Klarna &amp; Afterpay (BNPL)" icon={CalendarClock} note="Buy Now Pay Later methods are handled by Stripe. Once enabled in your Stripe Dashboard they appear automatically at checkout — no extra keys needed.">
          <ol className="text-sm font-light space-y-2 list-decimal ml-5">
            <li>Sign in to your <a href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noopener noreferrer" className="link-underline text-accent">Stripe Dashboard → Payment Methods</a>.</li>
            <li>Toggle on <strong>Klarna</strong> and <strong>Afterpay / Clearpay</strong>.</li>
            <li>Approve Stripe's short activation flow (business info, average order value, country).</li>
            <li>Done — the checkout page now shows Klarna and Afterpay as separate options for eligible orders.</li>
          </ol>
          <p className="text-xs font-light text-muted-foreground pt-2">Eligibility rules (min $35 for Klarna, $1–$2000 for Afterpay in the US) are enforced by Stripe automatically. Buyers see live installment previews.</p>
        </Card>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <button disabled={saving} onClick={save} className="w-full sm:w-auto bg-foreground text-background px-8 py-3 overline disabled:opacity-50" data-testid="save-settings">{saving ? 'Saving…' : 'Save All Settings'}</button>
        <span className="text-xs font-light text-muted-foreground">Secrets are stored masked. Blanks are ignored — retype to update.</span>
      </div>
    </div>
  );
}
